import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { maxNumberOfMessages } from './ReceiveMessages';
import Alert from '@mui/material/Alert';
import { Button, Tooltip, TextField, FormControlLabel, Switch, Snackbar, CircularProgress } from '@mui/material';
import { Replay } from '@mui/icons-material';
import { createServiceBusClient, createMessageReceiverAsync, createMessageSender } from "../modules/createServiceBusClient"
import { createEvent, createErrorEvent, createMessageEvent } from "../modules/logManager";

// const Alert = React.forwardRef(function Alert(props, ref) {
//     return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
// });


const Root = styled('div')({});

const Form = styled('form')({});

const FormItem = styled('div')(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const ProgressContainer = styled('div')(({ theme }) => ({
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(2),
}));

export default function ReplayMessages(props) {
    // const theme = useTheme();
    const { entityInfo, credentials, selectedEntity = null, onMessageEventReceived, setErrorMessage } = props;
    const [numberOfMessages, setNumberOfMessages] = useState(1);
    const [deleteFromSource, setDeleteFromSource] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [openNotification, setOpenNotification] = useState(false);
    const [receiveResult, setReceiveResult] = useState("Received messages for replay successfully");

    const handleNotificationClose = (event, reason) => {
        setOpenNotification(false);
    };

    const receiveMode = "peekLock";

    const receiveEvent = Object.assign({},
        createEvent(entityInfo, receiveMode),
        {
            entityName: `${entityInfo.entityName}/$deadLetterQueue`
        }
    );

    const completeEvent = Object.assign({},
        createEvent(entityInfo, "complete"),
        {
            entityName: `${entityInfo.entityName}/$deadLetterQueue`
        }
    );

    const subscriptionTopicEntityInfo = entityInfo.entityType === "subscription" ? Object.assign({}, entityInfo, {
        entityType: "topic",
        entityName: entityInfo.entityName.split("/")[0]
    }) : undefined;


    const sendEvent = Object.assign({},
        createEvent(subscriptionTopicEntityInfo || entityInfo, "send"),
        {
            entityName: `${(subscriptionTopicEntityInfo || entityInfo).entityName}`
        }
    );

    // callback to run when any error occurs while any receive operation is going
    const onReceiveError = (error) => {
        setErrorMessage(`Error receiving messages: ${(error.errors && error.errors.length > 0 ? error.errors[error.errors.length - 1].toString() : '') || error.message || (error.toString ? error.toString() : '') || JSON.stringify(error)}`)
        onMessageEventReceived(createErrorEvent(receiveEvent, entityInfo, error, {} , selectedEntity));
    };

    const replay = async () => {
        window.dataLayer && window.dataLayer.push({
            event: receiveMode === 'replay_messages',
            number_of_messages: numberOfMessages,
            delete_from_source: deleteFromSource,
        });
        setIsLoading(true);
        const serviceBusClient = createServiceBusClient(entityInfo, credentials, 3);
        const receiverOptions = {
            receiveMode,
            subQueueType: "deadLetter",
            maxWaitTimeInMs: 3600000, // Message read timeout 60 minutes
        };
        try {
            // create receiver
            const receiver = await createMessageReceiverAsync(serviceBusClient, entityInfo, receiverOptions);
            // create sender
            const sender = subscriptionTopicEntityInfo ? createMessageSender(serviceBusClient, subscriptionTopicEntityInfo) : createMessageSender(serviceBusClient, entityInfo);
            // receive specific number of messages
            const receivedMessages = await receiver.receiveMessages(numberOfMessages < 1 ? 1 : numberOfMessages, {
                maxWaitTimeInMs: 4000,
            });

            // callback to run when message is received by a message receiver
            // if receiveMode is peekLock, the handler also abandon the message
            const onMessageReceived = (receiver) => async (message) => {
                onMessageEventReceived(createMessageEvent(receiveEvent, message));
                if (!deleteFromSource) {
                    try {
                        await receiver.abandonMessage(message);
                    } catch (error) {
                        onMessageEventReceived(createErrorEvent(Object.assign({}, receiveEvent, { operation: "abandon" }), entityInfo, error, { message }, selectedEntity));
                    }
                }
                try {
                    await sender.sendMessages(message);
                    onMessageEventReceived(createMessageEvent(sendEvent, message));
                } catch (error) {
                    onMessageEventReceived(createErrorEvent(sendEvent, entityInfo, error, { message }, selectedEntity));
                }
                if (deleteFromSource) {
                    try {
                        await receiver.completeMessage(message);
                        onMessageEventReceived(createMessageEvent(completeEvent, message));
                    } catch (error) {
                        onMessageEventReceived(createErrorEvent(Object.assign({}, receiveEvent, { operation: "complete" }), entityInfo, error, { message }, selectedEntity));
                    }
                }
            };

            // no messages
            if (receivedMessages.length === 0) {
                setReceiveResult("No messages available in DLQ for replay")
                setOpenNotification(true);
                onMessageEventReceived(Object.assign({}, receiveEvent, {
                    time: new Date(),
                }));
            }
            else {
                // received messages
                setReceiveResult(`Successfully received ${receivedMessages.length} messages from DLQ to replay.`)
                setOpenNotification(true);
                // process received messages
                await Promise.all(receivedMessages.map(onMessageReceived(receiver)));
            }
            // refresh if there is a selected entity
            if(selectedEntity && selectedEntity.refresh)
            {
                selectedEntity.refresh();
            }
            // cleanup after receive specific number of messages
            setIsLoading(false);
            await Promise.all([receiver.close(), sender.close()]);
            await serviceBusClient.close();
        } catch (error) {
            onReceiveError(error);
            setIsLoading(false);
            await serviceBusClient.close();
        }

    };


    return (
        <Root>
            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={openNotification} autoHideDuration={6000} onClose={handleNotificationClose}>
                <Alert onClose={handleNotificationClose} severity="success" variant="filled">
                    {receiveResult}
                </Alert>
            </Snackbar>
            <Form noValidate autoComplete="off">
                <FormItem>
                    <TextField
                        size="small"
                        variant="outlined"
                        margin="dense"
                        label="Number of messages to replay"
                        value={numberOfMessages}
                        onChange={(e) => {setNumberOfMessages(Number(e.target.value) > maxNumberOfMessages ? maxNumberOfMessages : (Number(e.target.value) < 0 ? 1 : Number(e.target.value)))}}
                        InputProps={{ inputProps: { min: 1, max: maxNumberOfMessages } }}
                        type="number"
                        fullWidth
                    />
                </FormItem>

                <FormItem>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={deleteFromSource}
                                onChange={(e) => { setDeleteFromSource(e.target.checked) }}
                                name="deleteFromSource"
                                color="primary"
                            />
                        }
                        label="Delete from DLQ"
                    />
                </FormItem>

                <Tooltip title={entityInfo.isValid ? "" : "Please make sure you fill entity information details correctly."} placement="right">
                    <div style={{ display: "inline-block", padding: "0px", margin: "0px" }}>
                        <StyledButton  onClick={replay} disabled={!entityInfo.isValid || isLoading} variant="contained" color="primary"  endIcon={<Replay />}>
                            Replay
                    </StyledButton>
                    </div>
                </Tooltip>
                {isLoading
                    ?     <ProgressContainer>
                    <CircularProgress color="secondary" />
                </ProgressContainer>
                    : undefined
                }
            </Form>
        </Root>
    )
}

export { ReplayMessages };